import React, { useEffect, useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';

interface StatProps {
  value: string;
  label: string;
  numericValue?: number;
  prefix?: string;
  suffix?: string;
}

const AnimatedNumber: React.FC<{ target: number; prefix?: string; suffix?: string; inView: boolean }> = ({
  target,
  prefix = '',
  suffix = '',
  inView,
}) => {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const duration = 2000;
    const startTime = Date.now();

    const tick = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCurrent(Math.round(eased * target));
      if (progress < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [inView, target]);

  return (
    <span>
      {prefix}
      {current.toLocaleString()}
      {suffix}
    </span>
  );
};

const stats: StatProps[] = [
  { value: '2,400', label: 'Dealer Rooftops', numericValue: 2400 },
  { value: '75%', label: 'Market Share in Mexico', numericValue: 75, suffix: '%' },
  { value: '8', label: 'Product Verticals', numericValue: 8 },
  { value: 'MX$1.87B', label: 'Combined Year 3 Revenue Potential', numericValue: 1.87, prefix: 'MX$', suffix: 'B' },
];

const StatsBar: React.FC = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });

  return (
    <section id="stats" className="relative py-16 px-4 border-t border-b border-[#1F1F2E]">
      <div ref={ref} className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-4">
        {stats.map((stat, i) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.1 }}
            className="text-center"
          >
            <div className="font-space-grotesk text-3xl sm:text-4xl md:text-5xl font-bold bg-gradient-to-r from-[#3B82F6] to-[#8B5CF6] bg-clip-text text-transparent mb-2">
              {stat.numericValue && stat.numericValue > 10 ? (
                <AnimatedNumber
                  target={stat.numericValue}
                  prefix={stat.prefix}
                  suffix={stat.suffix}
                  inView={inView}
                />
              ) : (
                stat.value
              )}
            </div>
            <div className="text-xs text-[#A1A1AA]/60 uppercase tracking-wider">{stat.label}</div>
          </motion.div>
        ))}
      </div>
    </section>
  );
};

export default StatsBar;
